import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { cleanPhoneNumber } from 'helpers/parsing';
import { styled, useTheme } from '@mui/material/styles';
import { hasValue, isFunction } from 'swipeclock-frontend';
import { setGoToUserDetails } from 'store/appSlice';
import { useFetcher } from 'react-router-dom';
import { useFetcherStateForKey } from 'hooks/useNavigationState';
import { useGetLoginDetailsQuery } from 'services/masterDataApi';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import {
  useUserDetailsInputHandler,
  useUserDetailsValidateOnStart,
} from 'hooks/useUserDetailsInputHandler';
import UserDetailsProvider, { FORM_IDS } from 'providers/UserDetailsProvider';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import Button from 'components/Button';
import Divider from '@mui/material/Divider';
import ErrorIcon from '@mui/icons-material/Error';
import IconButton from '@mui/material/IconButton';
import NumberTextfield from 'components/uncontrolled/NumberTextfield';
import Textfield from 'components/uncontrolled/Textfield';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';

export default function UserDetails() {
  const fetcher = useFetcher({ key: 'logindetails' });
  const { success, error } = fetcher?.data ?? {};
  const { isError, isUninitialized, isSuccess } = useGetLoginDetailsQuery();
  const hasError = isError || isUninitialized || !!error;

  return (
    <UserDetailsProvider>
      <GridBox>
        <Header />
        {isSuccess && <LoginDetailsForm error={hasError} success={!!success} />}
        <Footer />
      </GridBox>
    </UserDetailsProvider>
  );
}

UserDetails.propTypes = {};

function LoginDetailsForm({ error, success }) {
  const { data } = useGetLoginDetailsQuery(); // cached prev by loader
  const fetcher = useFetcher({ key: 'logindetails' });
  const isLoading = useFetcherStateForKey('logindetails');

  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    validationRef,
    setDirty,
  } = useUserDetailsInputHandler({
    ids: Object.values(FORM_IDS),
    validationCallback,
  });

  const defaultValues = getDefaultValues(data);

  // Check if valid on start
  const isValidOnStart = useValidateForm(defaultValues, (errorBooleans) =>
    setDirty(errorBooleans)
  );

  return (
    <fetcher.Form method="post" action="/">
      <SuccessToast success={success} />
      <FormInputtGrid error={!!error}>
        {error && <ErroMessage />}
        <FormHeader>
          <SubTitle>Personal information for {data?.login || ''}</SubTitle>
          <Blurb>Use the fields below to update your details as needed.</Blurb>
        </FormHeader>
        <FirstNameField
          error={getError(FORM_IDS.FIRST_NAME)}
          id={FORM_IDS.FIRST_NAME}
          label="First Name"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.FIRST_NAME)}
          onChange={handleChange(FORM_IDS.FIRST_NAME)}
          placeholder="Enter your first name"
          defaultValue={defaultValues.firstName}
          required
          type="text"
          validationRef={validationRef}
        />
        <LastNameField
          error={getError(FORM_IDS.LAST_NAME)}
          id={FORM_IDS.LAST_NAME}
          label="Last Name"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.LAST_NAME)}
          onChange={handleChange(FORM_IDS.LAST_NAME)}
          placeholder="Enter your last name"
          defaultValue={defaultValues.lastName}
          required
          type="text"
          validationRef={validationRef}
        />
        <EmailField
          autoComplete="email"
          error={getError(FORM_IDS.EMAIL)}
          id={FORM_IDS.EMAIL}
          label="Email"
          maxLength={50}
          onBlur={handleBlur(FORM_IDS.EMAIL)}
          onChange={handleChange(FORM_IDS.EMAIL)}
          placeholder="Enter your email"
          defaultValue={defaultValues.email}
          required
          type="email"
          validationRef={validationRef}
        />
        <PhoneField
          autoComplete="tel"
          error={getError(FORM_IDS.PHONE_NUMBER)}
          formatType="phone"
          hint="Example: ( xxx ) xxx - xxxx"
          id={FORM_IDS.PHONE_NUMBER}
          label="Phone Number"
          maxLength={20}
          onBlur={handleBlur(FORM_IDS.PHONE_NUMBER)}
          onChange={handleChange(FORM_IDS.PHONE_NUMBER)}
          placeholder="Enter your phone number"
          validationRef={validationRef}
          defaultValue={defaultValues.phone}
          required={false}
        />
        <Button
          fullWidth
          name="intent"
          type="submit"
          value={'update-login-details'}
          endArrow
          disabled={(!isValidOnStart && disabled) || success}
          loading={isLoading}
          sx={{ mt: 1, gridArea: 'save' }}
        >
          SAVE
        </Button>
      </FormInputtGrid>
    </fetcher.Form>
  );
}

LoginDetailsForm.propTypes = {
  error: PropTypes.bool,
  success: PropTypes.bool,
};

function SuccessToast({ success }) {
  const fetcher = useFetcher({ key: 'logindetails' });
  const { announceSnackbar } = useSnackbarForMessage();
  const dispatch = useDispatch();

  useEffect(() => {
    if (success) {
      announceSnackbar(
        'Your personal details have been updated successfully.',
        'success'
      );
      dispatch(setGoToUserDetails({ goToResetPassword: false }));
    }

    // Return a cleanup function that gets called when the component unmounts
    return () => {
      if (success) {
        fetcher.submit({ idle: true }, { method: 'post', action: '/' });
      }
    };
  }, [success]);

  return null;
}

SuccessToast.propTypes = {
  success: PropTypes.bool,
};

function ErroMessage() {
  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap={1}
      sx={{ gridArea: 'error' }}
    >
      <ErrorIconWrapper />
      <ErrorMessageText>
        We couldn't update your details right now. Please try again, or reach
        out to our support team for assistance.
      </ErrorMessageText>
    </Stack>
  );
}

function Header() {
  const dispatch = useDispatch();
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: theme.spacing(1),
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            variant="body1"
            sx={{ color: theme.palette.custom.navyBlue.main }}
          >
            Update Personal Information
          </Typography>
        </Stack>
        <IconButton
          onClick={() => {
            dispatch(setGoToUserDetails({ goToUserDetails: false }));
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
    </>
  );
}

function Footer() {
  return (
    <>
      <Divider />
      <Box sx={{ height: '56px', width: '100%' }} />
    </>
  );
}

//##################################################################
// ### STYLED COMPONENTS
//##################################################################

// header divider content footer
// divider is incased inside the header component
const GridBox = styled(Box)`
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  height: 100%;
`;

const ErrorMessageText = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: theme.typography.fontWeightMedium,
}));

const ErrorIconWrapper = styled((props) => <ErrorIcon {...props} />)(
  ({ theme }) => ({
    color: theme.palette.error.main,
  })
);

const FormInputtGrid = styled((props) => <Box {...props} />, {
  shouldForwardProp: (prop) => prop !== 'error',
})(({ theme, error }) => ({
  display: 'grid',
  gridTemplateAreas: `
    ${error ? "'error'" : ''}
    'header'
    'firstname'
    'lastname'
    'email'
    'phone'
    'save'
    `,
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
}));

const FormHeader = styled(Box)(({ theme }) => ({
  gridArea: 'header',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const SubTitle = styled((props) => (
  <Typography variant="subtitle2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
  gridArea: 'subtitle',
}));

const Blurb = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
  gridArea: 'blurb',
}));

const FirstNameField = styled(Textfield)(() => ({
  gridArea: 'firstname',
}));

const LastNameField = styled(Textfield)(() => ({
  gridArea: 'lastname',
}));

const EmailField = styled(Textfield)(() => ({
  gridArea: 'email',
}));

const PhoneField = styled(NumberTextfield)(() => ({
  gridArea: 'phone',
}));

//##################################################################
// ### HELPERS
//##################################################################

const getDefaultValues = (data) => {
  const defaultValues = {
    [FORM_IDS.FIRST_NAME]: data?.firstName,
    [FORM_IDS.LAST_NAME]: data?.lastName,
    [FORM_IDS.EMAIL]: data?.email,
    [FORM_IDS.PHONE_NUMBER]: cleanPhoneNumber(data?.phone),
  };
  return defaultValues;
};

const validationCallback = (id, value) => {
  let error = null;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

  switch (id) {
    case FORM_IDS.FIRST_NAME:
      if (!hasValue(value)) {
        error = 'First name is required.';
      } else if (value.length > 50) {
        error = 'First name is too long.';
      }
      break;
    case FORM_IDS.LAST_NAME:
      if (!hasValue(value)) {
        error = 'Last name is required.';
      } else if (value.length > 50) {
        error = 'Last name is too long.';
      }
      break;
    case FORM_IDS.EMAIL:
      if (!hasValue(value)) {
        error = 'Email is required.';
      } else if (!emailRegex.test(value)) {
        error = 'Email is invalid.';
      }
      break;
    case FORM_IDS.PHONE_NUMBER:
      if (
        hasValue(cleanPhoneNumber(value)) &&
        !phoneRegex.test(cleanPhoneNumber(value))
      ) {
        error = 'Phone number is invalid.';
      }
      break;
    default:
      break;
  }
  return error;
};

let setDirty = false;
export const useValidateForm = (values, callback) => {
  const { valid, errors } = useUserDetailsValidateOnStart({
    ids: Object.values(FORM_IDS),
    validationCallback,
    values,
  });

  // We want to update all errors to dirty so validation happens on change events now.
  // errors are an object of type { id, page, validationError }
  // we only want to return the validationError as a boolean instead of a string
  // so we can use it to set the dirty state of the input
  if (!setDirty && isFunction(callback)) {
    const errorBooleans = errors.reduce((acc, obj) => {
      acc[obj.id] = !!obj.validationError;
      return acc;
    }, {});

    callback(errorBooleans);
    setDirty = true;
  }

  return valid;
};
